/**
 * statusHistory selectors (VE-26730). Per-TDO lookups are plain accessor
 * functions (cheap object reads — no memoization needed); the records arrays
 * are stored already merged and sorted by the reducer.
 */
import {
  REDACTION_STATUS_CORRECTION_NEEDED,
  resolveRedactionStatusKey,
} from '@common-modules/mediaDetails/redactionStatuses';
import {
  StatusHistoryCompleteness,
  StatusHistoryGate,
  StatusHistoryPage,
  StatusHistoryRecord,
} from '../models';
import { StatusHistoryStore, defaultState, namespace } from '../store';

const emptyPage: StatusHistoryPage = {
  records: [],
  count: 0,
  isLoading: false,
  error: null,
};

const local = (state: StatusHistoryStore) => state[namespace] ?? defaultState;

export const selectStatusHistoryGate = (
  state: StatusHistoryStore
): StatusHistoryGate => local(state).gate;

/** FR-7/US-9: true only once the registry id + published schema resolved. */
export const selectIsStatusHistoryEnabled = (
  state: StatusHistoryStore
): boolean => local(state).gate === 'enabled';

/**
 * Whether a page for this TDO has been fetched at all this session — as
 * distinct from one that came back empty, which the page shape alone cannot
 * tell apart (both are zero records and a zero count).
 *
 * The modal needs the difference to size itself: "no history" is a settled
 * answer that should take the room it needs, "not asked yet" is a blank that
 * should not shrink the dialog and then grow it back.
 */
export const selectHasFetchedStatusHistory = (
  state: StatusHistoryStore,
  tdoId: string
): boolean => local(state).historyByTdoId[tdoId] !== undefined;

/** SECURITY-11 funnel: whether this session has server-verified read access. */
export const selectIsTdoReadVerified = (
  state: StatusHistoryStore,
  tdoId: string
): boolean => local(state).readVerifiedTdoIds[tdoId] === true;

/** undefined = not fetched yet; null = fetched, TDO has no rows. */
export const selectLatestStatusRow = (
  state: StatusHistoryStore,
  tdoId: string
): StatusHistoryRecord | null | undefined => local(state).latestByTdoId[tdoId];

export interface LatestCorrectionNote {
  readonly note: string;
  readonly userName: string;
  readonly timestamp: string;
  /**
   * Display label frozen on the row that raised the note — the fallback for
   * rendering a status that has since been deleted, which resolves to no
   * current label at all.
   */
  readonly statusLabel: string;
}

/**
 * Pure BR-8 row-side condition: the row resolves to built-in Correction Needed
 * AND carries a note. Components that already select the (stable) latest row
 * should derive through this instead of selectLatestCorrectionNote, so
 * useSelector keeps a stable reference.
 */
export const getCorrectionNoteFromRecord = (
  row: StatusHistoryRecord | null | undefined
): LatestCorrectionNote | null => {
  // A cleared row keeps its note text; it just stops counting for anything.
  if (!row || !row.note || row.isDeleted) {
    return null;
  }
  const key = resolveRedactionStatusKey(row.statusKey);
  if (key !== REDACTION_STATUS_CORRECTION_NEEDED) {
    return null;
  }
  return {
    note: row.note,
    userName: row.userName,
    statusLabel: row.statusLabel,
    // Server-assigned time, deliberately NOT the client `timestamp` in the SDO
    // data: that field is written by the browser, so any holder of the org's
    // SDO roles can antedate a row. See the integrity note in models.
    timestamp: row.createdDateTime,
  };
};

/**
 * The note to surface for a Correction Needed file (BR-8 row-side condition).
 * Hosts must additionally verify the file's *current* status is still
 * Correction Needed (they own the live status) before showing the pop-up/icon.
 * NOTE: returns a fresh object — inside useSelector prefer
 * selectLatestStatusRow + getCorrectionNoteFromRecord.
 */
export const selectLatestCorrectionNote = (
  state: StatusHistoryStore,
  tdoId: string
): LatestCorrectionNote | null =>
  getCorrectionNoteFromRecord(local(state).latestByTdoId[tdoId]);

export const selectStatusHistory = (
  state: StatusHistoryStore,
  tdoId: string
): StatusHistoryPage => local(state).historyByTdoId[tdoId] ?? emptyPage;

export const selectStatusHistoryLoading = (
  state: StatusHistoryStore,
  tdoId: string
): boolean => selectStatusHistory(state, tdoId).isLoading;

export const selectStatusHistoryError = (
  state: StatusHistoryStore,
  tdoId: string
): string | null => selectStatusHistory(state, tdoId).error;

/**
 * Whether the loaded history covers the file's whole life.
 *
 * Answerable only from the OLDEST row, so it stays 'unknown' until the history
 * is fully paged — reporting 'partial' off a half-loaded page would call every
 * long history incomplete. Callers that need a verdict without paging should
 * fetch one row ascending; the read query already takes an order direction.
 *
 * 'partial' is a positive fact (an 'adopted' origin row records exactly where
 * Redact joined) and 'cleared' another (a tombstone records exactly where a
 * user cut the trail). Everything else with no origin row is 'unknown': a
 * history written before the field existed, or a file that predates the
 * feature and so never got an opening row at all. The UI is free to render
 * those the same — what it must not do is call any of them complete.
 */
/**
 * The history as the UI should show it: the soft-deleted rows dropped.
 *
 * Clearing tags rather than removes, so the raw page still holds what came
 * before. Filtering here rather than in the reducer keeps that intact — a row
 * cleared by mistake comes back the moment the flag is un-set.
 */
export const selectVisibleStatusHistory = (
  state: StatusHistoryStore,
  tdoId: string
): StatusHistoryPage => {
  const page = selectStatusHistory(state, tdoId);
  if (!page.records.some((record) => record.isDeleted)) {
    return page;
  }
  const records = page.records.filter((record) => !record.isDeleted);
  return {
    ...page,
    records,
    // The server count includes the hidden rows. Reporting it would leave
    // "Load more" offering pages that render nothing — and the tombstone marks
    // the end of what there is to see anyway.
    count: records.length,
  };
};

export const selectStatusHistoryCompleteness = (
  state: StatusHistoryStore,
  tdoId: string
): StatusHistoryCompleteness => {
  const page = selectVisibleStatusHistory(state, tdoId);
  if (page.records.length === 0 || page.records.length < page.count) {
    return 'unknown';
  }
  // Records are stored newest-first, so the opening row is the last one.
  const oldest = page.records[page.records.length - 1];
  if (oldest?.origin === 'created') {
    return 'complete';
  }
  // Both are a known point before which something existed that is not being
  // shown, but they are not the same fact and the UI does not say them the
  // same way: a clear has a tombstone row that already names it.
  if (oldest?.origin === 'cleared') {
    return 'cleared';
  }
  return oldest?.origin === 'adopted' ? 'partial' : 'unknown';
};
